/**
 * MaintainX-Style Layout Component
 * Enterprise-grade UI with icon sidebar, clean white content area
 * Inspired by MaintainX's professional maintenance software design
 */

import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  CalendarClock,
  Gavel,
  Wrench,
  Users,
  ShoppingCart,
  Package,
  Building2,
  Settings,
  GraduationCap,
  BarChart3,
  Calendar,
  FileText,
  Truck,
  Shield,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Bell,
  User,
  Menu,
  X,
  HardHat,
  Boxes,
  ClipboardList,
  DollarSign,
  History,
  Droplets,
  UserPlus,
  Briefcase,
  Code,
  FolderKanban,
  Heart,
  StickyNote,
  Globe,
  Plug,
  TrendingUp,
  Banknote,
  Trophy,
  Presentation
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardHeroBanner, { DashboardSearchBar } from "@/components/ui/DashboardHeroBanner";

// Map routes to hero sections
const routeToSection = {
  "/admin": "overview",
  "/admin/maintenance": "maintenance",
  "/admin/project-management": "maintenance",
  "/admin/assets": "equipment",
  "/admin/orders": "backorders",
  "/admin/backorders": "backorders",
  "/admin/payables": "backorders",
  "/admin/payables-desk": "backorders",
  "/admin/backorders/pipeline": "backorders",
  "/admin/backorders/live-inventory": "live-inventory",
  "/admin/contractors": "contractors",
  "/admin/suppliers": "suppliers",
  "/admin/supplier-orders": "supplier-orders",
  "/admin/commissions": "commissions",
  "/admin/hospitals": "hospitals",
  "/admin/users": "overview",
  "/admin/analytics": "analytics",
  "/admin/environmental": "compliance",
  "/admin/audit-log": "compliance",
  "/admin/security": "compliance",
  "/admin/settings": "overview",
  "/admin/training": "overview",
};

// Navigation items configuration
const navItems = [
  { 
    id: "dashboard", 
    label: "Dashboard", 
    icon: LayoutDashboard, 
    path: "/admin",
    exact: true
  },
  {
    id: "training",
    label: "Training & Tutorials",
    icon: GraduationCap,
    path: "/admin/training",
    subItems: [
      { id: "training-all", label: "All tracks", path: "/admin/training" },
      { id: "training-sourcing", label: "Sourcing Desk", path: "/admin/training?track=sourcing" },
      { id: "training-bid", label: "Bid Desk", path: "/admin/training?track=bid-desk" },
      { id: "training-pm", label: "Project Management", path: "/admin/training?track=project-management" },
      { id: "training-cards", label: "Quick reference cards", path: "/admin/training?view=cards" },
      { id: "training-records", label: "Training records", path: "/admin/training?view=records" },
    ],
  },
  { type: "divider" },
  { 
    id: "maintenance", 
    label: "Work Orders", 
    icon: Wrench, 
    path: "/admin/maintenance" 
  },
  { 
    id: "project-management", 
    label: "Project Management", 
    icon: FolderKanban, 
    path: "/admin/project-management" 
  },
  {
    id: "bid-desk",
    label: "Bid Desk",
    icon: Gavel,
    path: "/admin/bid-desk",
    badge: "New"
  },
  {
    id: "crew-schedule",
    label: "Crew Schedule",
    icon: CalendarClock,
    path: "/admin/crew-schedule",
    badge: "New"
  },
  {
    id: "contract-projects",
    label: "Contracts & Change Orders",
    icon: ClipboardList,
    path: "/admin/projects",
    badge: "New"
  },
  { 
    id: "proposals", 
    label: "Proposals", 
    icon: FileText, 
    path: "/admin/proposals" 
  },
  { 
    id: "assets", 
    label: "Assets", 
    icon: Boxes, 
    path: "/admin/assets" 
  },
  { 
    id: "orders", 
    label: "Orders", 
    icon: ShoppingCart, 
    path: "/admin/orders" 
  },
  { 
    id: "backorders", 
    label: "Sourcing Desk", 
    icon: Package, 
    path: "/admin/backorders",
    subItems: [
      { id: "sourcing-board", label: "Sourcing board", path: "/admin/backorders" },
      { id: "payables-audit", label: "Invoice Audit", path: "/admin/payables?view=audit" },
      { id: "payables-parties", label: "Paid & outstanding by supplier", path: "/admin/payables?view=parties" },
      { id: "payables-payments", label: "Payments & runs", path: "/admin/payables?view=payments" },
      { id: "payables-recon", label: "PO reconciliation", path: "/admin/payables?view=reconciliation" },
      { id: "payables-contracts", label: "Supplier contract payables", path: "/admin/payables-desk" },
    ],
  },
  {
    id: "live-inventory",
    label: "Live Inventory",
    icon: Package,
    path: "/admin/backorders/live-inventory",
    badge: "New"
  },
  { 
    id: "marketplace", 
    label: "Marketplace", 
    icon: ShoppingCart, 
    path: "/admin/marketplace",
    badge: "New"
  },
  {
    id: "solm-pricing",
    label: "Sol-M Pricing",
    icon: DollarSign,
    path: "/admin/solm-pricing",
    badge: "New"
  },
  {
    id: "microcare-pricing",
    label: "MicroCare Pricing",
    icon: DollarSign,
    path: "/admin/microcare-pricing",
    badge: "New"
  },
  { type: "divider" },
  { 
    id: "contractors", 
    label: "Contractors", 
    icon: HardHat, 
    path: "/admin/contractors" 
  },
  { 
    id: "suppliers", 
    label: "Suppliers", 
    icon: Truck, 
    path: "/admin/suppliers" 
  },
  {
    id: "supplier-orders",
    label: "Supplier Orders",
    icon: ClipboardList,
    path: "/admin/supplier-orders"
  },
  {
    id: "supplier-scorecard",
    label: "Supplier Scorecard",
    icon: Trophy,
    path: "/admin/supplier-scorecard",
    badge: "New"
  },
  {
    id: "supplier-spend",
    label: "Supplier Spend",
    icon: TrendingUp,
    path: "/admin/supplier-spend",
    badge: "Admin"
  },
  {
    id: "gross-profit",
    label: "Gross Profit",
    icon: TrendingUp,
    path: "/admin/gross-profit",
    badge: "New"
  },
  {
    id: "commissions",
    label: "Commissions",
    icon: DollarSign,
    path: "/admin/commissions"
  },
  {
    id: "crm",
    label: "CRM",
    icon: Briefcase,
    path: "/admin/crm",
    badge: "New"
  },
  { 
    id: "hospitals", 
    label: "Hospitals", 
    icon: Building2, 
    path: "/admin/hospitals" 
  },
  { type: "divider" },
  { 
    id: "recruitment", 
    label: "Recruitment", 
    icon: UserPlus, 
    path: "/contractor/recruitment" 
  },
  { 
    id: "analytics", 
    label: "Analytics", 
    icon: BarChart3, 
    path: "/admin/analytics" 
  },
  { 
    id: "users", 
    label: "Users", 
    icon: Users, 
    path: "/admin/users" 
  },
  {
    id: "team-approvals",
    label: "Team Portal",
    icon: UserPlus,
    path: "/admin/team-approvals",
    adminOnly: true, // super_admin OR hub_admin
    liveBadge: "team_pending", // resolved dynamically in the sidebar
  },
  { type: "divider" },
  { 
    id: "environmental", 
    label: "Water Testing", 
    icon: Droplets, 
    path: "/admin/environmental" 
  },
  { 
    id: "di-tanks", 
    label: "DI Tanks", 
    icon: Droplets, 
    path: "/admin/di-tanks" 
  },
  { 
    id: "audit-log", 
    label: "Audit Log", 
    icon: History, 
    path: "/admin/audit-log" 
  },
  { type: "divider", label: "Resources" },
  {
    id: "quickbooks",
    label: "QuickBooks",
    icon: Plug,
    path: "/admin/quickbooks"
  },
  { 
    id: "email-campaigns", 
    label: "Email Campaigns", 
    icon: Mail, 
    path: "/admin/email-campaigns" 
  },
  { 
    id: "trade-intelligence", 
    label: "Trade Intelligence", 
    icon: Globe, 
    path: "/admin/trade-intelligence" 
  },
  { 
    id: "quick-notes", 
    label: "Quick Notes", 
    icon: StickyNote, 
    path: "/admin/quick-notes" 
  },
  { 
    id: "letters", 
    label: "Letters", 
    icon: FileText, 
    path: "/admin/letters" 
  },
  { 
    id: "sales-playbook", 
    label: "Sales Playbook", 
    icon: FileText, 
    path: "/sales-playbook" 
  },
  { 
    id: "onboarding-playbook", 
    label: "Onboarding Playbook", 
    icon: ClipboardList, 
    path: "/onboarding-playbook" 
  },
  { 
    id: "investor-portal", 
    label: "Investor Portal", 
    icon: BarChart3, 
    path: "/investor",
    subItems: [
      {
        id: "investor-living-waters",
        label: "💧 Living Waters",
        path: "/living-waters",
        external: true,
        title: "Open the Living Waters × Bleu Flux marketing site (no password)"
      }
    ]
  },
  { 
    id: "investor-leads", 
    label: "Investor Leads", 
    icon: UserPlus, 
    path: "/admin/investor-leads" 
  },
  {
    id: "bioburden-visitors",
    label: "Bioburden Visitors",
    icon: UserPlus,
    path: "/admin/bioburden-visitors",
    badge: "New"
  },
  { 
    id: "philanthropy", 
    label: "Philanthropy", 
    icon: Heart, 
    path: "/admin/philanthropy",
    badge: "New"
  },
  {
    id: "demo-presentations",
    label: "Demo Presentations",
    icon: Presentation,
    path: "/admin/demo-presentations",
    badge: "New",
  },
  { type: "divider" },
  {
    id: "developer-notes",
    label: "Developer Notes",
    icon: Code,
    path: "/admin/developer-notes",
    demoOnly: true, // only visible when logged in as demo_readonly reviewer
    badge: "Tour",
  },
  { 
    id: "developer-hub", 
    label: "Developer Hub", 
    icon: Code, 
    path: "/developer-hub",
    external: true
  },
  { 
    id: "settings", 
    label: "Settings", 
    icon: Settings, 
    path: "/admin/settings" 
  },
];

// Sidebar Component
const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState({});
  const [teamPendingCount, setTeamPendingCount] = useState(0);

  // Poll pending team-approvals count for admins (badge on sidebar item)
  useEffect(() => {
    if (!["super_admin", "hub_admin"].includes(user?.role)) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("sterilemate_token");
        if (!token) return;
        const r = await fetch(`${API}/api/admin/team-pending/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setTeamPendingCount(d.count || 0);
        }
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, [user?.role]);

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const isSubItemActive = (subItem) => {
    const [path, search] = subItem.path.split("?");
    if (location.pathname !== path) return false;
    return search ? location.search.includes(search) : !location.search;
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Auto-expand items with active sub-items
  useEffect(() => {
    navItems.forEach(item => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(sub => location.pathname === sub.path);
        if (hasActiveSubItem) {
          setExpandedItems(prev => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [location.pathname]);

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 transition-all duration-300 z-50 flex flex-col ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <span className="text-white font-semibold text-lg">AskIsaac</span>
          </Link>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mx-auto">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-400 hover:text-white p-1 rounded hidden lg:block"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {navItems.map((item, index) => {
            // Project Manager scope — restrict sidebar to the PM Portal only.
            // Must run BEFORE the divider case so section dividers between
            // hidden groups don't leave floating separators.
            if (user?.role === "project_manager") {
              if (item.type === "divider") return null;
              const PM_ALLOWED = new Set([
                "dashboard",
                "training",
                "project-management",
                "bid-desk",
                "crew-schedule",
                "contract-projects",
                "proposals",
                "contractors",
                "suppliers",
                "settings",
              ]);
              if (!PM_ALLOWED.has(item.id)) return null;
            }

            if (item.type === "divider") {
              return (
                <li key={`divider-${index}`} className="my-3">
                  {item.label && !collapsed ? (
                    <div className="px-3 mb-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        {item.label}
                      </span>
                    </div>
                  ) : null}
                  <div className="h-px bg-slate-800 mx-2" />
                </li>
              );
            }

            // admin visibility gate (super_admin OR hub_admin)
            if (item.adminOnly && !["super_admin", "hub_admin"].includes(user?.role)) {
              return null;
            }
            // super-admin-only visibility gate
            if (item.superAdminOnly && user?.role !== "super_admin") {
              return null;
            }
            // demo-only visibility gate — only reviewer accounts see this item
            if (item.demoOnly && !user?.permissions?.includes("read_only_demo")) {
              return null;
            }

            const Icon = item.icon;
            const active = isActive(item);
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItems[item.id];

            // Resolve any live badge (numeric — e.g., team_pending count)
            let liveBadge = null;
            if (item.liveBadge === "team_pending" && teamPendingCount > 0) {
              liveBadge = teamPendingCount;
            }

            return (
              <li key={item.id}>
                {hasSubItems ? (
                  <>
                    <button
                      onClick={() => (collapsed ? navigate(item.path) : toggleExpand(item.id))}
                      className={`flex items-center justify-between w-full gap-3 px-3 py-2.5 rounded-lg transition-colors group ${
                        active
                          ? "bg-blue-600/20 text-blue-400"
                          : "text-slate-400 hover:bg-slate-800 hover:text-white"
                      }`}
                      title={collapsed ? item.label : undefined}
                      data-testid={`nav-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                        {!collapsed && (
                          <span className="text-sm font-medium truncate">{item.label}</span>
                        )}
                      </div>
                      {!collapsed && (
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </button>
                    {!collapsed && isExpanded && (
                      <ul className="mt-1 ml-7 space-y-1">
                        {item.subItems.map(subItem => {
                          const subActive = isSubItemActive(subItem);
                          const subClass = `block px-3 py-2 rounded-lg text-sm transition-colors ${
                            subActive
                              ? "bg-blue-600 text-white"
                              : "text-slate-400 hover:bg-slate-800 hover:text-white"
                          }`;
                          return (
                            <li key={subItem.id}>
                              {subItem.external ? (
                                <a
                                  href={subItem.path}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`${subClass} flex items-center justify-between gap-2`}
                                  data-testid={`nav-${subItem.id}`}
                                  title={subItem.title || subItem.label}
                                >
                                  <span className="truncate">{subItem.label}</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-70"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
                                </a>
                              ) : (
                                <Link
                                  to={subItem.path}
                                  className={subClass}
                                  data-testid={`nav-${subItem.id}`}
                                >
                                  {subItem.label}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group ${
                      active
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                    }`}
                    title={collapsed ? item.label : undefined}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-rose-500 text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {!collapsed && liveBadge && (
                      <span
                        className="ml-auto px-2 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full animate-pulse"
                        data-testid={`nav-badge-${item.id}`}
                      >
                        {liveBadge}
                      </span>
                    )}
                    {collapsed && liveBadge && (
                      <span
                        className="absolute right-1 top-1 min-w-[16px] h-4 px-1 text-[10px] font-bold bg-amber-500 text-white rounded-full flex items-center justify-center"
                        data-testid={`nav-badge-collapsed-${item.id}`}
                      >
                        {liveBadge}
                      </span>
                    )}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile & Logout */}
      <div className="border-t border-slate-800 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
              <User size={18} className="text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || user?.full_name || "Admin"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {user?.role?.replace("_", " ") || "Administrator"}
              </p>
            </div>
          </div>
        ) : null}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
          title="Logout"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

// Top Header Component
const TopHeader = ({ title, subtitle, actions, collapsed }) => {
  const { user } = useAuth();
  
  return (
    <header 
      className={`sticky top-0 z-40 bg-white border-b border-slate-200 h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${
        collapsed ? "lg:ml-16" : "lg:ml-64"
      }`}
      data-testid="top-header"
    >
      <div className="ml-10 lg:ml-0">
        <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{subtitle}</p>}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notifications */}
        <button 
          className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="notifications-btn"
        >
          <Bell size={20} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>
        
        {/* Custom Actions */}
        {actions}
      </div>
    </header>
  );
};

// Main Layout Export
export default function MaintainXLayout({ 
  children, 
  title = "Dashboard", 
  subtitle,
  actions 
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isReadOnlyDemo = user?.permissions?.includes("read_only_demo");

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Read-only demo banner — sticky top strip, visible on every admin page */}
      {isReadOnlyDemo && (
        <div
          className="sticky top-0 z-[60] bg-amber-500 text-slate-900 border-b-2 border-amber-600 shadow-md"
          data-testid="readonly-demo-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3 text-xs sm:text-sm font-semibold">
            <span className="inline-flex h-2 w-2 rounded-full bg-slate-900 animate-pulse" />
            <span className="uppercase tracking-widest text-[11px]">Read-Only Demo Mode</span>
            <span className="hidden sm:inline text-slate-800 font-normal">
              &middot; You are logged in as <strong>Demo Reviewer</strong>. Write actions are disabled.
            </span>
          </div>
        </div>
      )}

      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile unless menu open */}
      <div className={`lg:block ${mobileMenuOpen ? "block" : "hidden"}`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        <TopHeader 
          title={title} 
          subtitle={subtitle}
          actions={actions}
          collapsed={collapsed}
        />
        
        <main className="p-4 sm:p-6" data-testid="main-content">
          {/* Hero Banner */}
          <DashboardHeroBanner 
            portalType="admin" 
            activeSection={routeToSection[location.pathname] || "overview"}
          />
          
          {/* Search Bar Below Banner */}
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <DashboardSearchBar 
              placeholder="Search work orders, hospitals, contractors..."
              className="w-full sm:w-auto"
            />
          </div>
          
          {children}
        </main>
      </div>
    </div>
  );
}

// Export individual components for custom use
export { Sidebar, TopHeader };
